import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import Datagrid from "@ui/components/DataGrid";
import SearchBar from "@ui/components/SearchBar";
import { HEADINGS, searchMap } from "./constants";
import {
  removeAnalyte,
  setAnalytesList,
  unArchiveAnalyte,
} from "./_redux/actions";
import { renderAnalytesRow } from "./helpers";
import { ListButton } from "../../../../ui/components/ListButton";
import { ListHeader } from "../../../../ui/structures/ListHeader";
import { useFetch } from "../../../../hooks/fetch.hook";
import {
  deleteAnalyte,
  getAnalyteById,
  getAnalytes,
  modifyAnalyte,
} from "./_api";
import { ConfirmDelete } from "../../../../ui/components/ConfirmDelete";
import { Modal } from "../../../../ui/components/Modal";
import { ArchiveCheckbox } from "../../../../ui/components/ArchiveCheckbox";
import { getSelectedId, handleSearch } from "../../../../ui/helpers";
import { UnarchiveButton } from "../../../../ui/components/UnarchiveButton";
import { sortHeaderStyle } from "../../../constants";
import { TableSortLabel } from "@material-ui/core";

export const List = () => {
  const dispatch = useDispatch();
  const { request } = useFetch();

  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [initialData, setInitialData] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [unArchiving, setUnarchiving] = useState(false);
  const [field, setField] = useState("");
  const [direction, setDirection] = useState("");

  const handleModalClose = () => setConfirmDeleteOpen(false);

  const handleOpenModal = (id) => {
    setConfirmDeleteOpen(true);
    setSelectedItem(id);
  };

  const data = useSelector(({ analytes: { analytesList } }) => analytesList);

  const fetchAnalytes = () => {
    setLoading(true);
    request(getAnalytes, search, archived, field, field && direction)
      .then(
        (data) =>
          data &&
          data.map((item) => ({
            ...item,
            level_of_reporting: [
              item.reporting_value,
              item.reporting_unit,
            ].join(" "),
          }))
      )
      .then((data) => {
        if (!data) return;
        dispatch(setAnalytesList(data));
        setInitialData(data);
      })
      .finally(() => {
        setArchivedLoading(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAnalytes();
    // eslint-disable-next-line
  }, [search, archived, field, direction]);

  const showArchived = () => {
    setArchivedLoading(true);
    setArchived(!archived);
  };

  const unArchiveDisabled = useMemo(
    () => Object.values(selected).filter(Boolean).length !== 1,
    [selected]
  );

  const KEY_MAP = {
    "Analyte ID": data.map((item) => item.code),
    "Analyte Name": data.map((item) => item.name),
    Group: data.map((item) => item.group),
    Type: data.map((item) => item.type),
  };

  const resetSearch = () => {
    dispatch(setAnalytesList(initialData));
    setSearch("");
  };

  const renderButtons = () => (
    <>
      <div className="mr-10">
        <Link
          to={{
            pathname: `/add-analyte`,
          }}
          data-testid="new"
        >
          <ListButton label="New" />
        </Link>
      </div>
      {archived && (
        <UnarchiveButton
          handleClick={handleUnarchive}
          disabled={unArchiveDisabled}
          loading={unArchiving}
        />
      )}
    </>
  );

  const handleFocus = () => setFocused(true);

  const handleDelete = (id, setSubmitting) => {
    setSubmitting(true);
    request(deleteAnalyte, id)
      .then((data) => {
        if (!data) return;
        if (archived) {
          request(getAnalyteById, id).then(
            (data) => data && dispatch(unArchiveAnalyte(data))
          );
        } else dispatch(removeAnalyte(id));
      })
      .finally(() => {
        setSubmitting(false);
        setConfirmDeleteOpen(false);
      });
  };

  const handleUnarchive = () => {
    const analyteID = getSelectedId(selected);
    const status = (data.find(({ id }) => id === analyteID) || {}).status;
    if (status !== "archived") return;
    setUnarchiving(true);
    request(modifyAnalyte, { status: "active" }, analyteID)
      .then((data) => {
        if (!data) return;
        dispatch(unArchiveAnalyte(data));
        setSelected({});
      })
      .finally(() => setUnarchiving(false));
  };

  function renderHeaderWithSorting(headings) {
    return headings.map(([key, header]) => (
      <th
        key={key}
        style={sortHeaderStyle}
        className="px-5 text-nowrap"
        onClick={() => {
          setDirection(
            key !== field ? "desc" : direction === "desc" ? "asc" : "desc"
          );
          setField(key);
        }}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {key === field && <TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  return (
    <div className="row justify-content-center mt-10">
      <div className="col-12">
        {confirmDeleteOpen && (
          <Modal
            isOpen={confirmDeleteOpen}
            submitable
            onClose={handleModalClose}
            maxWidth="sm"
            modalContent={
              <ConfirmDelete
                handleClose={handleModalClose}
                handleSubmit={handleDelete}
                id={selectedItem}
                name={(data.find(({ id }) => id === selectedItem) || {}).name}
              />
            }
          />
        )}
        <div>
          <SearchBar
            onSearch={(data) => handleSearch(data, searchMap, setSearch)}
            clearSearch={resetSearch}
            keyMap={KEY_MAP}
            placeholder="Filter Analytes..."
            elevation={2}
            chipBackgroundColor="#E8F5E1"
            chipColor="#255915"
            focused={focused}
            setFocused={setFocused}
          />
        </div>
        <div className="bg-white rounded py-7 px-10">
          <ListHeader
            title="Analytes"
            renderButtons={renderButtons}
            handleFocus={handleFocus}
          />
          <ArchiveCheckbox
            archivedLoading={archivedLoading}
            archived={archived}
            showArchived={showArchived}
          />
          <Datagrid
            data={data}
            headings={HEADINGS}
            renderRow={renderAnalytesRow}
            renderHeaderWithSorting={renderHeaderWithSorting}
            deletable
            handleDelete={handleOpenModal}
            editable
            selectable
            selected={selected}
            setSelected={setSelected}
            loading={loading}
            link="analytes"
          />
        </div>
      </div>
    </div>
  );
};
